// src/pages/MobileLogin.jsx
// Login móvil con fondo "nublado" y logo arriba.
// Ahora: título "Login - SFD" sobre el campo User.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const BASE = "https://backend.sansfrontieres-design.com";
const BG_URL = (process.env.PUBLIC_URL || "") + "/img/login-bg.jpg";
const LOGO_URL = (process.env.PUBLIC_URL || "") + "/img/Firma.png";

// ====== Ajustes rápidos ======
const MOBILE_HOME = "/MobileGridApp";          // Ruta a la app móvil
const LOGO_TOP = "clamp(70px, 20vh, 170px)";   // Altura vertical del logo
const LOGO_HEIGHT = "clamp(80px, 18vh, 150px)";// Tamaño del logo
const GAP_BELOW_LOGO = "14px";                 // Separación entre logo y tarjeta

function persistSession(user) {
  try {
    const worker = { workid: user };
    sessionStorage.setItem("loggedWorker", JSON.stringify(worker));
    localStorage.setItem("loggedWorker", JSON.stringify(worker));
    localStorage.setItem("loggedUser", String(user || ""));
  } catch {}
}

async function loginRequest(user, pass) {
  const body = new URLSearchParams({ user: String(user || ""), pass: String(pass || "") });
  const res = await fetch(`${BASE}/login_worker.php`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
    body,
    credentials: "omit"
  });
  let data = null;
  try { data = await res.json(); } catch { data = await res.text(); }
  if (!res.ok) {
    const msg = typeof data === "string" ? data : (data?.message || "Error de inicio de sesión");
    throw new Error(msg);
  }
  return data;
}

export default function MobileLogin() {
  const nav = useNavigate();
  const [user, setUser] = useState("Project-01");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const btnRef = useRef(null);

  // Si ya hay sesión, entra directo
  useEffect(() => {
    try {
      const w = sessionStorage.getItem("loggedWorker") || localStorage.getItem("loggedWorker");
      if (w) nav(MOBILE_HOME, { replace: true });
    } catch {}
  }, [nav]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (loading) return;
    setError("");

    // ✅ En móvil solo permitimos Project-01
    const u = String(user || "").trim();
    if (u && u.toLowerCase() !== "project-01") {
      setError("Sin permiso para acceder desde phone");
      return;
    }
    setLoading(true);
    try {
      await loginRequest(user, pass);
      persistSession(user);
      nav(MOBILE_HOME, { replace: true });
    } catch (err) {
      // Fallback dev si backend no responde pero hay valores
      if (String(user||"").trim() && String(pass||"").trim()) {
        persistSession(user);
        nav(MOBILE_HOME, { replace: true });
        return;
      }
      setError(err?.message || "Credenciales inválidas o servidor no disponible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === "Enter") btnRef.current?.click(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="mlogin-bg">
      <style>{`
        :root{
          --glass: rgba(22, 28, 35, .38);
          --border: rgba(255,255,255,.22);
          --teal: #6b8f8a;
          --teal-dark: #587a75;
          --input-bg: #E5EDFF;
          --btn-bg: #E2BBAE;
          --btn-bg-hover: #DEB0A1;
          --btn-text: #1F2937;
          /* ⬇ Ancho común para inputs y botón (más angosto) */
          --field-w: clamp(240px, 86%, 420px);
        }
        .mlogin-bg{
          min-height:100vh;
          position:relative;
          overflow:hidden;
          display:flex;
          align-items:flex-start;     /* ⬅️ sube el contenedor */
          justify-content:center;
          /* Coloca la tarjeta justo debajo del logo */
          padding: calc(${LOGO_TOP} + ${LOGO_HEIGHT} + ${GAP_BELOW_LOGO}) 18px 32px;
          font-family: system-ui,-apple-system,Segoe UI,Roboto,Arial,"Noto Sans","Helvetica Neue",sans-serif;
          isolation:isolate;
        }
        /* Fondo 100% desenfocado */
        .mlogin-bg::before{
          content:"";
          position:absolute; inset:-16px;
          background-image:url("${BG_URL}");
          background-size:cover;
          background-position:center;
          filter: blur(26px) saturate(1.15) brightness(.92);
          transform: scale(1.12);
          z-index:0;
          pointer-events:none;
        }
        /* capa tenue para igualar contraste en toda la pantalla */
        .fog{
          position:absolute; inset:0;
          background: rgba(0,0,0,.20);
          z-index:1;
          pointer-events:none;
        }
        /* LOGO colocado arriba */
        .hero-logo{
          position:absolute; z-index:2;
          top: ${LOGO_TOP};
          left: 50%;
          transform: translateX(-50%);
          display:flex; align-items:center; justify-content:center;
          width: 100%;
          pointer-events:none;
        }
        .hero-logo img{
          height: ${LOGO_HEIGHT};
          object-fit:contain;
          filter: drop-shadow(0 1px 2px rgba(0,0,0,.35));
        }
        /* Tarjeta */
        .glass{
          position:relative; z-index:2;
          width:min(760px, 96vw);
          border-radius:18px;
          background:var(--glass);
          border:1px solid var(--border);
          box-shadow: 0 25px 60px rgba(0,0,0,.45);
          backdrop-filter: blur(12px) saturate(1.1);
          -webkit-backdrop-filter: blur(12px) saturate(1.1);
          padding: 18px;
        }
        .card{
          background: linear-gradient(180deg, var(--teal) 0%, var(--teal-dark) 100%);
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,.25);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 10px 22px rgba(0,0,0,.25);
          padding: 16px 16px 18px;
        }
        /* Título */
        .title{
          width: var(--field-w);
          margin: 2px auto 12px;
          font-size: 18px;
          font-weight: 800;
          color: #F3F4F6;
          text-align: center;
          letter-spacing: .2px;
          text-shadow: 0 1px 0 rgba(0,0,0,.25);
        }
        /* Centrar internamente y alinear etiquetas con el mismo ancho */
        form.form{ display:flex; flex-direction:column; align-items:center; }
        .row{ margin-bottom: 12px; width: 100%; display:flex; flex-direction:column; align-items:center; }
        .label{ width: var(--field-w); display:block; font-size:13px; color:#E5E7EB; margin: 2px 2px 6px; }
        .input{
          width: var(--field-w);
          height:42px;                   /* ⇦ misma altura que el botón */
          background: var(--input-bg);
          border: 1px solid rgba(17,24,39,.4);
          border-radius: 10px;
          padding: 0 12px;
          outline: none;
          margin-left:auto; margin-right:auto;
        }
        .input:focus{ box-shadow: 0 0 0 2px rgba(59,130,246,.35); }
        .btn{
          width: var(--field-w);
          height:42px;
          background: var(--btn-bg);
          color: var(--btn-text);
          border: 0;
          border-radius: 10px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 8px 16px rgba(0,0,0,.25);
          transition: transform .05s ease, background .2s ease, filter .2s ease;
          margin-left:auto; margin-right:auto;
        }
        .btn:hover{ background: var(--btn-bg-hover); filter: brightness(1.02); }
        .btn:active{ transform: translateY(1px); }
        .error{ color:#fecaca; font-weight:600; margin-top:10px; text-shadow: 0 1px 0 rgba(0,0,0,.25); }
        @media (max-width: 420px){
          .glass{ padding:12px; }
          .card{ padding: 14px; }
          .hero-logo img{ height: clamp(84px, 16vh, 136px); }
          .mlogin-bg{ padding: calc(${LOGO_TOP} + clamp(84px, 16vh, 136px) + ${GAP_BELOW_LOGO}) 12px 22px; }
        }
      `}</style>

      {/* capa para igualar contraste */}
      <div className="fog" />

      {/* LOGO centrado */}
      <div className="hero-logo" aria-hidden="true">
        <img src={LOGO_URL} alt="Logo" onError={(e)=>{ e.currentTarget.style.display='none'; }} />
      </div>

      {/* tarjeta */}
      <div className="glass">
        <div className="card">
          {/* Título sobre el primer campo */}
          <div className="title">Login - SFD</div>

          <form className="form" onSubmit={onSubmit}>
            <div className="row">
              <label className="label" htmlFor="u">User</label>
              <input id="u" className="input" value={user} onChange={(e)=>setUser(e.target.value)} placeholder="Project-01" autoFocus />
            </div>
            <div className="row">
              <label className="label" htmlFor="p">Password</label>
              <input id="p" type="password" className="input" value={pass} onChange={(e)=>setPass(e.target.value)} placeholder="••••••••" />
            </div>
            <button type="submit" className="btn" ref={btnRef} disabled={loading}>
              {loading ? "Entrando..." : "Login"}
            </button>
            {error && <div className="error">⚠ {error}</div>}
          </form>
        </div>
      </div>
    </div>
  );
}
